/**
 * LedgerStatementView — the complete Tally Ledger view for one ledger:
 * opening balance, every posting with its contra "Particulars" ledger and a
 * running balance, then the closing balance and period totals.
 *
 * This is what a Trial Balance row drills down to. Every row is itself
 * clickable and carries voucherGuid, which is the key for the next level
 * (the full voucher).
 */

import React, { useCallback, useRef, useState } from 'react';
import './VoucherTable.css';
import './DrillDown.css';

const fmt = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return '';
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

const fmtTotal = (n) =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Tags the path from `el` up to <body> so the print stylesheet can isolate it:
 * the element itself, every ancestor, and every sibling that must drop out of
 * layout. Returns the undo function.
 *
 * This runs against the live DOM rather than through React state because it
 * has to touch nodes React does not own here (the modal overlay, the dashboard
 * panels, <body>'s other children) and must be reverted the instant the print
 * dialog closes, within the same synchronous turn as window.print().
 */
function markPrintChain(el) {
  if (!el) return () => {};

  const touched = [];
  const mark = (node, cls) => {
    if (!node.classList.contains(cls)) {
      node.classList.add(cls);
      touched.push([node, cls]);
    }
  };

  mark(el, 'dd-printing');

  let node = el;
  while (node.parentElement) {
    const parent = node.parentElement;
    for (const sibling of parent.children) {
      if (sibling !== node) mark(sibling, 'dd-print-hidden');
    }
    if (parent === document.body) break;
    mark(parent, 'dd-print-ancestor');
    node = parent;
  }

  return () => touched.forEach(([n, c]) => n.classList.remove(c));
}

const Meta = ({ label, value }) =>
  value === null || value === undefined || value === '' ? null : (
    <div className="dd-meta-item">
      <span className="dd-meta-label">{label}</span>
      <span className="dd-meta-value">{value}</span>
    </div>
  );

const LedgerStatementView = ({ data, loading, loadingAll = false, error, onOpenVoucher, onLoadAll, onNextPage, onPrevPage }) => {
  const [printing, setPrinting] = useState(false);
  const areaRef = useRef(null);

  /**
   * Pull every remaining entry first, then hand off to the browser's print
   * dialog. Printing the page as-is would stop at whatever the screen had
   * loaded — a ledger that ends mid-period without saying so is worse than
   * one that takes a moment longer to prepare.
   *
   * The `dd-printing` marker is what the print stylesheet targets, and it is
   * set on THIS statement's own container. Two statements can be mounted at
   * once — the Ledger panel keeps its selection while a Trial Balance
   * drill-down modal is open on top of it — and a stylesheet keyed on the
   * shared container class would put both ledgers on the paper.
   */
  const handlePrint = useCallback(async () => {
    setPrinting(true);
    try {
      if (onLoadAll) await onLoadAll();
      // Let React commit the appended rows before the print dialog snapshots
      // the document, otherwise the paper copy is the pre-fetch version.
      await new Promise((resolve) => setTimeout(resolve, 0));
      const undo = markPrintChain(areaRef.current);
      try {
        window.print();
      } finally {
        // print() blocks until the dialog closes in every current browser,
        // but unwind even if it throws so the screen is never left marked.
        undo();
      }
    } finally {
      setPrinting(false);
    }
  }, [onLoadAll]);

  if (loading) return <div className="vt-state vt-loading">Loading ledger…</div>;
  if (error) return <div className="vt-state vt-error">Failed to load ledger: {error}</div>;
  if (!data) return <div className="vt-state vt-empty">No ledger selected.</div>;

  const { ledger, opening, closing, totals, rows = [], count, limit, period, offset = 0 } = data;
  const truncated = count > rows.length;
  const hasPagination = count > limit;
  const busy = printing || loadingAll;

  const periodLabel = period?.from || period?.to
    ? `${period.from ? fmtDate(period.from) : 'Beginning'} — ${period.to ? fmtDate(period.to) : 'Today'}`
    : 'All dates';

  return (
    <div className="dd-print-area" ref={areaRef}>
      {/* Screen-only toolbar. */}
      <div className="dd-no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.6rem' }}>
        <button type="button" className="dd-btn" onClick={handlePrint} disabled={busy}>
          {busy
            ? (truncated ? `Preparing all ${new Intl.NumberFormat('en-IN').format(count)} entries…` : 'Preparing…')
            : '🖨 Print Ledger'}
        </button>
      </div>

      {/* Paper-only letterhead — hidden on screen by .dd-print-header. */}
      <div className="dd-print-header">
        <p className="dd-print-company">{ledger.companyName || ''}</p>
        <p className="dd-print-title">
          Ledger Account: {ledger.name}
          {ledger.groupName ? ` (${ledger.groupName})` : ''}
        </p>
        <p className="dd-print-period">Period: {periodLabel}</p>
      </div>

      <div className="dd-meta">
        <Meta label="Ledger" value={ledger.name} />
        <Meta label="Group" value={ledger.groupName} />
        <Meta label="Account Type" value={ledger.accountType} />
        <Meta label="GSTIN" value={ledger.gstNumber} />
        <Meta label="Period" value={periodLabel} />
        <Meta label="Opening Balance" value={`₹ ${fmtTotal(Math.abs(opening.signed))} ${opening.side}`} />
        <Meta label="Closing Balance" value={`₹ ${fmtTotal(Math.abs(closing.signed))} ${closing.side}`} />
        <Meta label="Entries in Period" value={new Intl.NumberFormat('en-IN').format(count)} />
      </div>

      {rows.length === 0 ? (
        <div className="vt-state vt-empty">
          No postings in this period — this ledger carries an opening balance only.
        </div>
      ) : (
        <>
          <p className="dd-hint">Click any row to open the original voucher.</p>
          <div className="voucher-table-wrap">
            <table className="voucher-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Particulars</th>
                  <th>Vch Type</th>
                  <th>Vch No.</th>
                  <th className="vt-amount-col">Debit</th>
                  <th className="vt-amount-col">Credit</th>
                  <th className="vt-amount-col">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="dd-boundary-row">
                  <td colSpan={4}>Opening Balance</td>
                  <td className="vt-amount vt-out">{fmt(opening.debit)}</td>
                  <td className="vt-amount vt-in">{fmt(opening.credit)}</td>
                  <td className="dd-running">
                    {fmtTotal(Math.abs(opening.signed))}<span className="dd-side">{opening.side}</span>
                  </td>
                </tr>

                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="dd-clickable"
                    onClick={() => onOpenVoucher && onOpenVoucher(r)}
                    title="Open the original voucher"
                  >
                    <td className="vt-date">{fmtDate(r.date)}</td>
                    <td className="vt-party" title={r.particulars}>
                      <span className="dd-link">{r.particulars}</span>
                      {r.isCancelled && <span className="dd-tag dd-tag-cancelled">Cancelled</span>}
                    </td>
                    <td className="vt-narration">{r.voucherType || '—'}</td>
                    <td className="vt-voucher">{r.voucherNumber || '—'}</td>
                    <td className="vt-amount vt-out">{fmt(r.debit)}</td>
                    <td className="vt-amount vt-in">{fmt(r.credit)}</td>
                    <td className="dd-running">
                      {fmtTotal(r.runningBalance)}<span className="dd-side">{r.runningSide}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="dd-boundary-row">
                  <td colSpan={4}>Period Total</td>
                  <td className="vt-amount vt-out">{fmtTotal(totals.debit)}</td>
                  <td className="vt-amount vt-in">{fmtTotal(totals.credit)}</td>
                  <td className="dd-running" />
                </tr>
                <tr className="dd-boundary-row">
                  <td colSpan={4}>Closing Balance</td>
                  <td className="vt-amount vt-out">{fmt(closing.debit)}</td>
                  <td className="vt-amount vt-in">{fmt(closing.credit)}</td>
                  <td className="dd-running">
                    {fmtTotal(Math.abs(closing.signed))}<span className="dd-side">{closing.side}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {hasPagination && (
            <div className="dd-hint" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
              <p style={{ margin: 0 }}>
                Showing entries {new Intl.NumberFormat('en-IN').format(offset + 1)} to {new Intl.NumberFormat('en-IN').format(Math.min(count, offset + limit))} of{' '}
                {new Intl.NumberFormat('en-IN').format(count)} (page size {limit}). Opening,
                closing and period totals above cover <strong>all</strong> {new Intl.NumberFormat('en-IN').format(count)} entries.
                Printing loads every entry first.
              </p>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="button"
                  className="dd-btn"
                  onClick={onPrevPage}
                  disabled={busy || offset === 0}
                >
                  &larr; Previous
                </button>
                <button
                  type="button"
                  className="dd-btn"
                  onClick={onNextPage}
                  disabled={busy || offset + limit >= count}
                >
                  Next &rarr;
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LedgerStatementView;
